import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  Skeleton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../utils/firebase";
import { useAuth } from "../../contexts/AuthContext";
import { UserData, UserRole, updateUserRole } from "../../utils/userRoles";
import { themeColors } from "../../utils/theme";

/**
 * AdminPanel component
 *
 * This component allows admins to manage users and their roles.
 * It should only be accessible to admin users.
 */
const AdminPanel: React.FC = () => {
  const { isAdmin, currentUser } = useAuth();
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch all users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const usersCollection = collection(db, "users");
      const userSnapshot = await getDocs(usersCollection);
      const usersList = userSnapshot.docs.map((doc) => doc.data() as UserData);

      setUsers(usersList);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch users");
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  // Load users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Handle role change dialog
  const handleOpenDialog = (user: UserData) => {
    setSelectedUser(user);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
  };

  // Change user role
  const handleChangeRole = async () => {
    if (!selectedUser) return;

    try {
      setActionLoading(true);

      // Determine new role (toggle between admin and user)
      const newRole =
        selectedUser.role === UserRole.ADMIN ? UserRole.USER : UserRole.ADMIN;

      // Update user role
      await updateUserRole(selectedUser.uid, newRole);

      // Update local state
      setUsers(
        users.map((user) =>
          user.uid === selectedUser.uid ? { ...user, role: newRole } : user
        )
      );

      handleCloseDialog();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to change user role"
      );
      console.error("Error changing user role:", err);
    } finally {
      setActionLoading(false);
    }
  };

  // If user is not an admin, show access denied message
  if (!isAdmin()) {
    return (
      <Paper elevation={3} sx={{ p: 4, maxWidth: 800, mx: "auto", mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Admin Panel
        </Typography>
        <Alert severity="error">
          Access denied. You must be an admin to view this page.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 800, mx: "auto", mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        User Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="outlined"
        onClick={() => fetchUsers()}
        sx={{ mb: 2 }}
        disabled={loading}
      >
        Refresh Users
      </Button>

      {loading ? (
        <Box sx={{ my: 4 }}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={400}
            sx={{
              borderRadius: 2,
              bgcolor: `${themeColors.accent}40`,
              '&::after': {
                background: `linear-gradient(90deg, transparent, ${themeColors.primary}20, transparent)`,
              },
            }}
          />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.uid}>
                  <TableCell>{user.displayName}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.role}
                      color={
                        user.role === UserRole.ADMIN ? "primary" : "default"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleOpenDialog(user)}
                      disabled={user.uid === currentUser?.uid} // Can't change own role
                    >
                      Change Role
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Role change confirmation dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Change User Role</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to{" "}
            {selectedUser?.role === UserRole.ADMIN
              ? "remove admin privileges from"
              : "make"}{" "}
            <strong>{selectedUser?.displayName}</strong>{" "}
            {selectedUser?.role === UserRole.ADMIN ? "" : "an admin"}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleChangeRole}
            color="primary"
            disabled={actionLoading}
            variant="contained"
          >
            {actionLoading ? (
              <Skeleton
                variant="text"
                width={60}
                height={24}
                sx={{
                  bgcolor: `${themeColors.background}40`,
                  '&::after': {
                    background: `linear-gradient(90deg, transparent, ${themeColors.background}60, transparent)`,
                  },
                }}
              />
            ) : (
              "Confirm"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdminPanel;
